import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { logger } from "../lib/logger";

/**
 * Emergent Google OAuth landing page.
 * After the user signs in on auth.emergentagent.com, they are redirected back to
 * THIS app with `#session_id=<id>` in the URL fragment. We exchange it for a
 * persistent session_token (httpOnly cookie), hydrate AuthContext, then push the
 * user into the dashboard.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refresh } = useAuth();
  // useRef (not useState) so the flag is set synchronously and survives StrictMode double-invoke
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match?.[1];

    if (!sessionId) {
      navigate("/login", { replace: true });
      return;
    }

    (async () => {
      try {
        const { data } = await api.post(
          "/auth/google/session",
          null,
          { headers: { "X-Session-ID": sessionId } }
        );
        // Hydrate AuthContext from the canonical /auth/me (uses the new cookie)
        await refresh();
        // Strip the fragment + send the user home
        const target = data.user?.role === "admin" ? "/admin" : "/dashboard";
        navigate(target, { replace: true });
      } catch (err) {
        logger.error("Google sign-in exchange failed", err);
        navigate("/login?error=google", { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4">
      <div className="premium-card p-10 sm:p-14 text-center max-w-md">
        <Loader2 className="mx-auto text-brand-primary animate-spin" size={36} strokeWidth={1.4} />
        <h1 className="font-serif-display text-2xl mt-5">Signing you in…</h1>
        <p className="text-brand-muted mt-2 text-sm">
          One moment — we're welcoming you back to Dkarez.
        </p>
      </div>
    </div>
  );
}
